import { Advice, FieldsInfo } from "@antv/ava";
import { EChartsOption } from "echarts-for-react";


type EChartAdvice = {name: string, description?: string, options?: EChartsOption, references?: any}
type AdviceConverter = (advice: Advice, df_info: FieldsInfo) => EChartAdvice | null
type EChartTransformSortConfig = {dimension: any, order: 'asc' | 'desc'}

function isTimeSeriesChart(advice: Advice, df_info: FieldsInfo) {
    const xAxisFieldInfo = df_info.find(({name}) => name === advice.spec?.encode.x)
    return ["date", "time"].includes(xAxisFieldInfo?.type || "")
    
}

function isMultiSeriesAdvice (advice: Advice) {
    return advice.spec?.encode.x && advice.spec?.encode.color
}

function standardizeAvaAdvisorDataType(t: string) {
    switch (t) {
        case 'date': {
            return 'time'
        }
        case 'string': {
            return 'ordinal'
        }
        case 'integer': {
            return 'int'
        }
        default:
        return t
    }
}

function getRawDataset({df_info , id = 'raw'}: {df_info: FieldsInfo, id?: string}) {
    const source = df_info[0].rawData.map((_, idx) => {
        return df_info.map(({name, rawData}) => {
            return {
                [name]: rawData[idx]
            }
        }).reduce((v1, v2) => Object.assign(v1, v2))
    })
    return {
        id,
        dimensions: df_info.map(({name, recommendation}) => {
            return {name, type: standardizeAvaAdvisorDataType(recommendation)}
        }),
        source
    }
}

function getTransformedDataset ({id, sourceDatasetId, sourceDatasetIndex, config}: {id?: string, sourceDatasetId?: string, sourceDatasetIndex?: number, config: any}) {
    return {
        id,
        fromDatasetId: sourceDatasetId,
        fromDatasetIndex: sourceDatasetIndex,
        transform: config
    }
}

function getSortedDataset ({id, sourceDatasetId, sourceDatasetIndex, configs}: {id?: string, sourceDatasetId?: string, sourceDatasetIndex?: number, configs: EChartTransformSortConfig | EChartTransformSortConfig[]}) {
    return getTransformedDataset({id, sourceDatasetId, sourceDatasetIndex, config: {
        type: 'sort',
        config: configs
    }})
}

function getFilteredDataset ({id, sourceDatasetId, sourceDatasetIndex, configs}: {id?: string, sourceDatasetId?: string, sourceDatasetIndex?: number, configs: any}) {
    return getTransformedDataset({id, sourceDatasetId, sourceDatasetIndex, config: {
        type: 'filter',
        config: configs
    }})
}

function getDatasetOptions (advice: Advice, df_info: FieldsInfo) {
    const dataset = [
        getRawDataset({df_info, id: 'raw'}),
        getSortedDataset({
            id: "sorted-raw", 
            configs: { 
                dimension: advice.spec?.encode.x, 
                order: 'asc' 
            }
        })
        
    ]
    
    if (isMultiSeriesAdvice(advice)) {
        const xAxisFieldInfo = df_info.find(({name}) => name === advice.spec?.encode.color)
        // if (xAxisFieldInfo === null || (xAxisFieldInfo?.distinct && xAxisFieldInfo?.distinct > 100)) {
        //     // Should not generate too many series chart
        //     return null
        // }
        
        const sortedDimensionByValue = Object.entries(xAxisFieldInfo?.valueMap || {}).sort((v1, v2) => v2[1] - v1[1] )
        
        dataset.push(...sortedDimensionByValue.map((k) => {
            return getFilteredDataset({
                id: `${xAxisFieldInfo?.name}-${k[0]}`,
                sourceDatasetId: "sorted-raw",
                configs: { dimension: xAxisFieldInfo?.name, eq: k[0] }
            })
        }))
    }
    return dataset
}

function getBaseTimeSeriesOptions (advice: Advice, df_info: FieldsInfo) {
    // todo: double check later
    const markPoint = {
        data: [
            {
                name: 'maximum',
                type: 'max'
            }, 
            {
                name: 'coordinate',
                coord: [10, 20]
            }, {
                name: 'fixed x position',
                yAxis: 10,
                x: '90%'
            }, 
            
            {
                name: 'screen coordinate',
                x: 100,
                y: 100
            }
        ]
    }
    const dataset = getDatasetOptions(advice, df_info)
    
    
    return {
        dataset,
        markPoint,
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'cross'
            },
        },
        grid: {width: "75%"},
        legend: {
            top: 'middle',
            right: "0%",
            orient: "vertical",
            type: 'scroll',
            
        },
        xAxis: { type: 'time'  },
        yAxis: {},
    }
    
}

function getSeriesOptions ({advice, df_info, chartType, extraSeriesOptions}:{advice: Advice, df_info: FieldsInfo, chartType: string, extraSeriesOptions?: any}) {
    const baseSeriesOptions = { 
        type: chartType,
        encode: {
            x: advice.spec?.encode.x,
            y: advice.spec?.encode.y,
            itemGroupId: advice.spec?.encode.color
        },
        ...extraSeriesOptions
        
    }
    if (isMultiSeriesAdvice(advice)) {
        const xAxisFieldInfo = df_info.find(({name}) => name === advice.spec?.encode.color)
        
        return Object.entries(xAxisFieldInfo?.valueMap || {}).sort((v1, v2) => v2[1] - v1[1] ).map((v) => v[0]).map((k) => {
            return {
                ...baseSeriesOptions, 
                name: k,
                datasetId: `${xAxisFieldInfo?.name}-${k}`,
            }
        })
    }
    
    
    return [{...baseSeriesOptions, name: advice.spec?.encode.y, datasetIndex: 1}]
}


const ADVICE_CONVERTERS: Record<string, AdviceConverter> = {
    'line_chart': (advice, df_info) => {
        if (isTimeSeriesChart(advice, df_info)) {
            return {
                name: "Line Chart",
                options: {
                    ...getBaseTimeSeriesOptions(advice, df_info),
                    series: getSeriesOptions({advice, df_info, chartType: 'line'})
                }
            }
        }
        return null
    },
    'bar_chart': (advice, df_info) => {
        return {
            name: "Bar Chart",
            options: {
                dataset: getDatasetOptions(advice, df_info),
                
                legend: {},
                tooltip: {},
                xAxis: { type: 'category'},
                yAxis: { type: 'value'},
                series: getSeriesOptions({advice, df_info, chartType: 'bar'})
            }
        }
    },
    'pie_chart': (advice, df_info) => {
        return {
            name: "Pie Chart",
            options: {
                dataset: [
                    getRawDataset({df_info, id: 'raw'}),
                    getSortedDataset({
                        id: "sorted-raw", 
                        configs: { 
                            dimension: advice.spec?.encode.y, 
                            order: 'asc' 
                        }
                    })
                    
                    
                ],
                // grid: {width: "75%"},
                legend: {
                    top: 'middle',
                    right: "0%",
                    orient: "vertical",
                    type: 'scroll',
                    selector: [
                        {
                            type: 'all',
                            // can be any title you like
                            title: 'All'
                        },
                        {
                            type: 'inverse',
                            // can be any title you like
                            title: 'Inv'
                        }
                    ],
                    // data: sortedDimensionByValue.map((v) => {
                    //   return {name: v[0]}
                    // }),
                    // selected: sortedDimensionByValue.map((v, idx) => {
                    //   return {[v[0]]: idx < 10 }
                    // }).reduce((v1, v2) => Object.assign(v1, v2))
                    // data: Object.entries(xAxisFieldInfo?.valueMap || {}).sort((v1, v2) => v1[1] - v2[1] )
                },
                // legend: { },
                tooltip: {},
                series: [{ 
                    type: 'pie',
                    encode: {
                        itemName: advice.spec?.encode.color,
                        value: advice.spec?.encode.y,
                    },
                    radius: "70%",
                    // roseType: 'radius',
                    datasetIndex: 1
                }]
            }
        }
    },
}

function convertAdviseToEchartOptions(advice: Advice, df_info: FieldsInfo): EChartAdvice | null {
    const getConvertor = (chartType?: string): AdviceConverter | null => chartType ? ADVICE_CONVERTERS[chartType] : null
    
    const convertor = getConvertor(advice.type) // || getConvertor(advice.spec?.type)
    
    let eChartAdvice = null
    if (convertor !== undefined && convertor !== null) {
        eChartAdvice = convertor(advice, df_info)
        
        if (eChartAdvice !== null) {
            eChartAdvice.references = {avaAdvice: advice}
            return eChartAdvice
        } 
    }
    
    return eChartAdvice
    
}

function heatmapChartOptionsGenerator(df_info: FieldsInfo): EChartAdvice | null {
    const numericField = df_info.find(({recommendation}) => {
        return recommendation === 'float' || recommendation === 'integer'
    }) 
    
    if (numericField === undefined) {
        return null
    }
    
    return {
        name: "Heatmap",
        options: {
            dataset: [
                getRawDataset({df_info, id: 'raw'}),
            ],
            legend: {},
            tooltip: {},
            xAxis: { type: 'category', axisLabel: {rotate: 45}},
            yAxis: { type: 'category'},
            grid: {
                height: '50%',
                top: '10%'
            },
            visualMap: {
                min: numericField.minimum,
                max: numericField.maximum,
                calculable: true,
                orient: 'horizontal',
                left: 'center',
                bottom: '15%'
            },
            series: [
                {
                    type: 'heatmap',
                    
                    data: df_info[0].rawData.map((_, idx) => {
                        return df_info.sort((v1, v2) => {
                            
                            return (v1.type === 'string' ? -1 : 1) - (v2.type === 'string' ? -1 : 1)
                        }).map(({ rawData}) => {
                            return rawData[idx]
                        })
                    })
                    
                }
            ]
        }
    }
}

function generateEchartAdvices({avaAdvices, df_info}: {avaAdvices?: Advice[], df_info: FieldsInfo}): EChartAdvice[] {
    const advices = avaAdvices?.map((advice) => convertAdviseToEchartOptions(advice, df_info)).filter((v) => v!==null) || []
    
    // heatmap chart
    // purpose: show relation between 2 dimension
    // apply rules: 1 metric, 2 low (>= 50 distinct values) cardinality dimensions
    if (
        df_info.length === 3 
        && df_info.filter(({recommendation}) => recommendation === 'float' || recommendation === 'integer').length === 1
        && df_info.filter(({recommendation}) => recommendation === 'string').length === 2
    ) {
        const heatmapOptions = heatmapChartOptionsGenerator(df_info)
        if (heatmapOptions !== null) {
            advices.push(heatmapOptions)
        }
        
    }
    
    
    return advices
    
}


export {generateEchartAdvices}